import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import {
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcCheckBoxComponent,
} from "@barthauer/abc/ui-components";
import { FlexLayoutModule } from "ngx-flexible-layout";
import { Observable, of, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import { IconsSvg } from "../../../../models/icons-svg";
import {
  defaultLabels,
  Extenstions,
  GeoDsDocumentViewModel,
  IActionButtonsMenuOutput,
  IContextMenuItems,
  Labels,
} from "../../document.editor.models";

@Component({
    selector: "abc-common-editors-documents-list",
    templateUrl: "./list.component.html",
    styleUrls: ["./list.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        _AbcButtonComponent,
        MatIconModule,
        FlexLayoutModule,
        _AbcCheckBoxComponent,
        AbcTextBoxComponent,
    ]
})
export class ListComponent implements OnInit, OnChanges {
  @Input() dataSource: GeoDsDocumentViewModel[] = [];
  @Input() allowMultipleSelection = false;
  @Input() allowSearch = true;
  @Input() actionsContextMenu = false;
  @Input() height = "100%";
  @Input() labels?: Labels = defaultLabels;
  @Input() isContextMenuVisible = false;
  @Input() customStyle: string;
  @Input() elementToCheck: string;

  public specificButtonsValue: IContextMenuItems[] = []

  @Input() set specificButtons(val: (doc?: GeoDsDocumentViewModel) => IContextMenuItems[] | Array<(doc?: GeoDsDocumentViewModel) => IContextMenuItems[]>) {
    this.specificButtonsValue =
      (Array.isArray(val)
        ? val
          .map(fn => fn(this.selectedDocument))
          .reduce((acc, items) => acc.concat(items), [])
        : val
          ? val(this.selectedDocument)
          : [])
  }

  get specificButtons(): IContextMenuItems[] {
    return this.specificButtonsValue;
  }

  @Input() selectedDocumentsIds: string[] = [];
  @Output() selectedDocumentsIdsChange = new EventEmitter<string[]>();

  @Output() previewDocument = new EventEmitter<string>();
  @Output() downloadDocument = new EventEmitter<string>();
  @Output() openDeleteDocumentPopup = new EventEmitter<string>();
  @Output() goToDetails = new EventEmitter<string>();
  @Output() openActionButtonsMenu =
    new EventEmitter<IActionButtonsMenuOutput>();
  @Output() rowDblClick = new EventEmitter<GeoDsDocumentViewModel>();
  public selectedDocument!: GeoDsDocumentViewModel;
  public searchTerm = new Subject<string>();
  public dataSourceToFilter: GeoDsDocumentViewModel[] = [];

  constructor(private iconRegistry: MatIconRegistry, private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.iconRegistry.addSvgIconLiteral(
      "pdf",
      this.sanitizer.bypassSecurityTrustHtml(IconsSvg.PDF)
    );
    this.iconRegistry.addSvgIconLiteral(
      "word",
      this.sanitizer.bypassSecurityTrustHtml(IconsSvg.WORD)
    );
    this.iconRegistry.addSvgIconLiteral(
      "xls",
      this.sanitizer.bypassSecurityTrustHtml(IconsSvg.XLS)
    );
    this.iconRegistry.addSvgIconLiteral(
      "image",
      this.sanitizer.bypassSecurityTrustHtml(IconsSvg.IMAGE)
    );

    if (this.allowSearch) {
      this.search(this.searchTerm).subscribe((results) => {
        this.dataSource = [];
        if (results !== null) {
          if (results.length > 0) {
            this.dataSource = results;
          }
        } else {
          this.dataSource = [...this.dataSourceToFilter];
        }
        this.cdr.markForCheck();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dataSource && changes.dataSource?.currentValue?.length > 0) {
      this.dataSourceToFilter = [...changes.dataSource.currentValue];
    }
  }

  public identify(index, item) {
    return item.Id;
  }

  public getSVGIcon(fileName: string): string {
    if (fileName && fileName.includes(".")) {
      const fileNameToArray = fileName ? fileName.split(".") : "";
      const fileType = fileNameToArray
        ? fileNameToArray.slice(-1)[0].toLowerCase()
        : "";

      if (fileType === "pdf") {
        return "pdf";
      }
      if (["xls", "xlsx"].includes(fileType)) {
        return "xls";
      }
      if (Extenstions.images.includes(fileType)) {
        return "image";
      }
      if (["doc", "docx", "txt"].includes(fileType)) {
        return "word";
      }
    } else {
      return "";
    }
  }

  public onRowDblClick(event: GeoDsDocumentViewModel): void {
    this.rowDblClick.emit(event);
  }

  public onOpenActionButtonsMenu(
    event: any,
    doc: GeoDsDocumentViewModel
  ): void {
    this.selectedDocument = doc;
    this.openActionButtonsMenu.emit({
      target: event.event.currentTarget,
      selectedDocument: doc,
    });
  }

  public onItemCheckChanged(event, documentId: string): void {
    if (event.value) {
      this.selectedDocumentsIds.push(documentId);
    } else {
      this.selectedDocumentsIds = this.selectedDocumentsIds.filter(
        (id) => id !== documentId
      );
    }
    this.selectedDocumentsIdsChange.emit(this.selectedDocumentsIds);
  }

  public search(terms: Observable<string>) {
    return terms.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => this.searchEntries(term))
    );
  }

  public searchEntries(term: string): Observable<GeoDsDocumentViewModel[]> {
    if (!term) {
      return of(null);
    }
    term = term.toUpperCase();
    return of(
      this.dataSourceToFilter.filter(
        (a) =>
          a.FileName?.toUpperCase().indexOf(term) >= 0 ||
          a.IdRefPersonInsertFullName?.toUpperCase().indexOf(term) >= 0 ||
          a.IdRefPersonInsertEmail?.toUpperCase().indexOf(term) >= 0 ||
          a.SysDateInsert?.toString().toUpperCase().indexOf(term) >= 0 ||
          a.Size?.toUpperCase().indexOf(term) >= 0
      )
    );
  }

  //We added this method to handle delete button in the search field
  public ChangeSearchValue(searchTerm: string): void {
    if (searchTerm.trim() === "") {
      this.searchTerm.next(searchTerm);
    }
  }

  /**
   * Handles scroll events on the box element to isolate scrolling from the parent container.
   * Prevents event propagation to ensure only the box scrolls when hovered.
   * @param event - The wheel event object containing scroll delta values.
   */
  public onBoxScroll(event: WheelEvent): void {
    event.stopPropagation();

    const element = event.target as HTMLElement;
    element.scrollTop += event.deltaY;
  }
}
